import { TriStateEnum } from '@/app/entities/TriState'
import { useAuth } from '@/app/hooks/useAuth'
import { configService } from '@/app/services/configService'
import { UpdateConfigProductRequest } from '@/app/services/configService/updateConfigProduct'
import type { ProductImageVariant } from '@/app/services/configService/uploadProductImage'
import { PartnerType } from '@/app/utils/partners.enum'
import { useToast } from '@/view/components/ui/use-toast'
import { zodResolver } from '@hookform/resolvers/zod'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { SubmitHandler, useForm } from 'react-hook-form'
import { z } from 'zod'

const imageField = z
  .union([z.instanceof(File), z.string(), z.null()])
  .optional()

const schema = z.object({
  label: z.string().optional(),
  subtitle: z.string().optional(),
  description: z.string().optional(),
  image: imageField,
  imageBlack: imageField,
})

type IProduct = {
  name: PartnerType
  label?: string
  itens: any[]
  tableId?: string
  delay?: number
  isActive?: boolean
  state?: TriStateEnum
  image?: string
  imageBlack?: string
  subtitle?: string
  description?: string
  color?: string
  createLink?: boolean
  externalData?: {
    partnerCanEdit?: boolean
    description?: string
    [key: string]: any
  } | null
}
export type FormSchema = z.infer<typeof schema>

const isNewImageFile = (file?: File): file is File => !!file && file.size > 0

export const useConfigPartner = (
  product: IProduct,
  onOpenChange: (open: boolean) => void,
) => {
  const queryClient = useQueryClient()
  const { isSuperAdmin } = useAuth()

  const [isColorModalOpen, setIsColorModalOpen] = useState(false)
  const [isToggleStateModalOpen, setIsToggleStateModalOpen] = useState(false)
  const [partnerColor, setPartnerColor] = useState<string | undefined>(
    product?.color ?? product?.externalData?.color ?? undefined,
  )

  const [createLink, setCreateLink] = useState(false)
  const [productState, setProductState] = useState<TriStateEnum>(
    product?.state ?? TriStateEnum.OFF,
  )

  const { toast } = useToast()

  const partnerCanEdit = product?.externalData?.partnerCanEdit ?? false

  const form = useForm<FormSchema>({
    resolver: zodResolver(schema),
    defaultValues: {
      label: partnerCanEdit
        ? (product?.label ?? '')
        : (product?.externalData?.title ?? ''),
      subtitle: partnerCanEdit
        ? (product?.subtitle ?? product?.name ?? '')
        : (product?.externalData?.subtitle ?? ''),
      description: product?.description ?? '',
      image: product?.image ?? '',
      imageBlack: product?.imageBlack ?? '',
    },
  })

  const { mutateAsync: removeProductImage, isPending: isRemovingProductImage } =
    useMutation({
      mutationFn: async ({
        type,
        path,
        variant,
      }: {
        type: PartnerType
        path: string
        variant?: ProductImageVariant
      }) => {
        return configService.removeProductImage(type, path, variant)
      },
    })

  const {
    mutateAsync: uploadProductImage,
    isPending: isUploadingProductImage,
  } = useMutation({
    mutationFn: async ({
      image,
      variant,
    }: {
      image: File
      variant?: ProductImageVariant
    }) => {
      return configService.uploadProductImage(
        product?.name as PartnerType,
        image,
        variant,
      )
    },
  })

  const { mutateAsync: updateProduct, isPending: isUpdatingProduct } =
    useMutation({
      mutationFn: async (input: UpdateConfigProductRequest) => {
        return configService.updateConfigProduct(input)
      },
    })

  const { mutateAsync: patchProductState, isPending: isPatchingState } =
    useMutation({
      mutationFn: async (state: TriStateEnum) => {
        return configService.patchConfigProducts({
          type: product?.name as PartnerType,
          state,
          isActive: product?.isActive ?? false,
        })
      },
    })

  const handleProductStateChange = async (state: TriStateEnum) => {
    const previousState = productState
    setProductState(state)
    try {
      await patchProductState(state)
      queryClient.invalidateQueries({ queryKey: ['productsConfig'] })
    } catch (error) {
      setProductState(previousState)
      toast({
        title: 'Erro ao atualizar estado do produto',
        variant: 'destructive',
      })
    }
  }

  const { mutateAsync: patchCreateLink, isPending: isPatchingCreateLink } =
    useMutation({
      mutationFn: async (createLinkValue: boolean) => {
        return configService.updateConfigProduct({
          type: product?.name as PartnerType,
          createLink: createLinkValue,
        })
      },
    })

  const handleCreateLinkChange = async (value: boolean) => {
    const previousValue = createLink
    setCreateLink(value)
    try {
      await patchCreateLink(value)
      queryClient.invalidateQueries({ queryKey: ['productsConfig'] })
    } catch (error) {
      setCreateLink(previousValue)
      toast({
        title: 'Erro ao atualizar link de autocontração',
        variant: 'destructive',
      })
    }
  }

  const handleClose = () => {
    form.reset()
    onOpenChange(false)
  }

  const handleUpdateProduct: SubmitHandler<FormSchema> = async (data) => {
    const type = product?.name as PartnerType

    const payload: UpdateConfigProductRequest = {
      type,
      createLink,
      color: partnerColor || '',
      ...(partnerCanEdit
        ? {
            subtitle: data.subtitle,
            label: data.label,
            description: data.description,
          }
        : {}),
    }
    try {
      if (data.image instanceof File && isNewImageFile(data.image)) {
        await uploadProductImage({ image: data.image, variant: 'image' })
      } else if (data.image === null && product?.image) {
        await removeProductImage({
          type,
          path: product?.image,
          variant: 'image',
        })
      }

      if (data.imageBlack instanceof File && isNewImageFile(data.imageBlack)) {
        await uploadProductImage({
          image: data.imageBlack,
          variant: 'imageBlack',
        })
      } else if (data.imageBlack === null && product?.imageBlack) {
        await removeProductImage({
          type,
          path: product?.imageBlack,
          variant: 'imageBlack',
        })
      }

      await updateProduct(payload)
      await queryClient.invalidateQueries({ queryKey: ['productsConfig'] })
      handleClose()
      toast({ title: 'Configurações salvas com sucesso' })
    } catch (error) {
      toast({ title: 'Erro ao salvar configurações', variant: 'destructive' })
    }
  }

  const makeRemoveHandler =
    (field: 'image' | 'imageBlack') =>
    (e: React.MouseEvent<HTMLButtonElement>) => {
      e.preventDefault()
      e.stopPropagation()
      form.setValue(field, null)
    }

  const handleRemoveImage = makeRemoveHandler('image')
  const handleRemoveImageBlack = makeRemoveHandler('imageBlack')

  useEffect(() => {
    if (product) {
      form.reset({
        label: partnerCanEdit
          ? (product.label ?? String(product.name))
          : (product.externalData?.title ?? ''),
        subtitle: partnerCanEdit
          ? (product.subtitle ?? '')
          : (product.externalData?.subtitle ?? ''),
        description: product.description ?? '',
        image: product.image ?? '',
        imageBlack: product.imageBlack ?? '',
      })

      setPartnerColor(product.color || product.externalData?.color || undefined)
      if (product.createLink) setCreateLink(product.createLink)
      setProductState(product.state ?? TriStateEnum.OFF)
    }
  }, [product])

  const isSaving =
    isUpdatingProduct || isUploadingProductImage || isRemovingProductImage

  return {
    form,
    isColorModalOpen,
    setIsColorModalOpen,
    isToggleStateModalOpen,
    setIsToggleStateModalOpen,
    partnerColor,
    setPartnerColor,
    isSaving,
    createLink,
    handleUpdateProduct,
    handleRemoveImage,
    setCreateLink,
    productState,
    handleProductStateChange,
    isPatchingState,
    isSuperAdmin,
    handleCreateLinkChange,
    isPatchingCreateLink,
    handleClose,
    partnerCanEdit,
    handleRemoveImageBlack,
  }
}
